/**
 * Provides the `versionCompare` utility function for comparing two software version number strings based on a specified comparison operator.
 * @module versionCompare
 */
/**
 * Compares two software version number strings (e.g., "1.7.1", "1.2.42") based on a specified comparison operator.
 *
 * **Behavior:**
 * 1.  **Input:** Takes two version strings (`v1`, `v2`) and a comparison `operator` (e.g., '>', '<=', '='). Defaults operator to '='.
 * 2.  **Parsing & validation:**
 *  - Splits each version string into parts using the dot (`.`) as a delimiter.
 *  - Validates that no empty segments exist (e.g., from '..', '.1', '1.'), throwing an error if found.
 *  - Converts the string parts to numbers. Throws an error if any part is not a valid number (e.g., "1.a.2").
 * 3.  **Comparison logic:**
 *  - Compares the numeric parts of `v1` and `v2` segment by segment from left to right.
 *  - If a segment differs, determines the result (-1 if v1 < v2, 1 if v1 > v2).
 *  - If one version has more segments than the other, missing segments are treated as 0 for comparison (e.g., "1.0" is considered equal to "1.0.0").
 *  - Returns 0 if all corresponding segments are equal.
 * 4.  **Operator evaluation:** Based on the numeric comparison result (-1, 0, or 1) and the specified `operator`, returns the final boolean result (`true` or `false`). Supports standard operators (`<`, `<=`, `>`, `>=`, `=`, `!=`) and textual aliases (`lt`, `le`, `gt`, `ge`, `eq`, `ne`, `<>`). Throws an error for invalid operators.
 *
 * **Summary table:**
 *
 * | `v1`      | `v2`      | `operator` | `versionCompare( v1, v2, operator )` result | Notes                                    |
 * | :-------- | :-------- | :--------- | :------------------------------------------ | :--------------------------------------- |
 * | `'1.2.3'` | `'1.2.3'` | `'='`      | `true`                                      | Equal versions                           |
 * | `'1.2'`   | `'1.2.0'` | `'eq'`     | `true`                                      | Different length, effectively equal      |
 * | `'1.10'`  | `'1.2'`   | `'>'`      | `true`                                      | Compares 10 vs 2                         |
 * | `'1.2'`   | `'1.10'`  | `'<'`      | `true`                                      | Compares 2 vs 10                         |
 * | `'2.0.0'` | `'1.9.9'` | `'gt'`     | `true`                                      | Major version difference                 |
 * | `'1.0'`   | `'1.1'`   | `'lt'`     | `true`                                      | Minor version difference                 |
 * | `'1.2.3'` | `'1.2.3'` | `'<='`     | `true`                                      | Equal satisfies Less Than or Equal       |
 * | `'1.2.2'` | `'1.2.3'` | `'le'`     | `true`                                      | Less Than satisfies Less Than or Equal   |
 * | `'1.2.4'` | `'1.2.3'` | `'>='`     | `true`                                      | Greater Than satisfies Greater or Equal  |
 * | `'1.0'`   | `'1.0.0'` | `'ge'`     | `true`                                      | Equal satisfies Greater Than or Equal    |
 * | `'1.2.3'` | `'1.2.4'` | `'!='`     | `true`                                      | Not equal                                |
 * | `'1.0'`   | `'1.0.0'` | `'ne'`     | `false`                                     | Effectively equal                        |
 * | `'1.a'`   | `'1.0'`   | `'='`      | `Error`                                     | Invalid character 'a'                    |
 * | `'1..0'`  | `'1.0'`   | `'='`      | `Error`                                     | Empty segment '..'                       |
 * | `'1.0'`   | `'1.0'`   | `'~'`      | `Error`                                     | Invalid operator                         |
 *
 * @since 5.3 Initial implementation.
 * @param {string} v1 - The first version number string (e.g., "1.10.2"). Must contain only digits and dots, with no empty segments.
 * @param {string} v2 - The second version number string (e.g., "1.2.10"). Must follow the same format rules as `v1`.
 * @param {VersionCompareOperator} [operator='='] - The comparison operator. Can be one of: `<`, `lt`, `<=`, `le`, `>`, `gt`, `>=`, `ge`, `=`, `==`, `eq`, `!=`, `<>`, `ne`. Defaults to `=`.
 * @return {boolean} The boolean result of comparing `v1` and `v2` using the specified `operator`.
 * @throws {Error} If either `v1` or `v2` has an invalid format (non-numeric parts, empty segments like '..' or leading/trailing '.').
 * @throws {Error} If the provided `operator` is not one of the recognized comparison strings.
 *
 * @example Importing the versionCompare utility
 * ```typescript
 * import { versionCompare } from '@automator/shared/utils/version-compare';
 * ```
 *
 * @example Greater than comparison
 * Checks if "1.10.2" is greater than "1.2.10".
 * ```typescript
 * versionCompare( '1.10.2', '1.2.10', '>' );
 * // => true (because 10 is greater than 2 in the second segment)
 * ```
 *
 * @example Less than or equal comparison with different lengths
 * Checks if "2.1" is less than or equal to "2.1.0".
 * ```typescript
 * versionCompare( '2.1', '2.1.0', '<=' );
 * // => true (treated as equal because missing segments are considered 0)
 * ```
 *
 * @example Equality check with different lengths
 * Checks if "1.0" is equal to "1.0.0".
 * ```typescript
 * versionCompare( '1.0', '1.0.0', '=' );
 * // => true
 * ```
 *
 * @example Not equal comparison
 * Checks if "3.5" is not equal to "3.5.1".
 * ```typescript
 * versionCompare( '3.5', '3.5.1', '!=' );
 * // => true
 * ```
 *
 * @example Handling invalid version format
 * Shows an error being thrown for a version with letters.
 * ```typescript
 * try {
 *     versionCompare( '1.a.2', '1.2.0' );
 * } catch ( error ) {
 *     console.error( error ); // Logs Error: Invalid version number format: Contains non-numeric parts...
 * }
 * ```
 *
 * @example Handling invalid operator
 * Shows an error being thrown for an unrecognized operator.
 * ```typescript
 * try {
 *     versionCompare( '1.0', '1.0', 'approx' ); // 'approx' is not a valid operator
 * } catch ( error ) {
 *     console.error( error ); // Logs Error: Invalid comparison operator provided: approx
 * }
 * ```
 *
 * @remarks
 * - Version segments are compared numerically (e.g., '1.10' > '1.2').
 * - Leading zeros in segments are ignored during numerical conversion (e.g., '1.01' is treated as '1.1').
 * - The function requires valid version strings containing only digits and dots, without consecutive or leading/trailing dots.
 */
export function versionCompare(v1, v2, operator = '=') {
    /**
     * Inner helper function to compare version numbers numerically.
     * @param {string} version1 - The first version number string to compare.
     * @param {string} version2 - The second version number string to compare.
     * @return {number} -1 if v1 < v2, 1 if v1 > v2, 0 if v1 == v2.
     * @throws {Error} if parts are not numeric or if segments are empty.
     */
    const getComparisonResult = (version1, version2) => {
        // Split version numbers into string parts first
        const stringParts1 = version1.split('.');
        const stringParts2 = version2.split('.');
        // Check if any part is an empty string (caused by '..', '.1', '1.')
        if (stringParts1.some(part => part === '') || stringParts2.some(part => part === '')) {
            throw new Error(`Invalid version number format: Contains empty segments (e.g., '..', leading/trailing '.'). v1: "${version1}", v2: "${version2}"`);
        }
        // Convert parts to numbers AFTER validation
        const parts1 = stringParts1.map(Number);
        const parts2 = stringParts2.map(Number);
        // Check for NaN resulting from non-numeric parts (e.g., "1.a.2")
        // This check might be slightly redundant now but kept for robustness
        if (parts1.some(isNaN) || parts2.some(isNaN)) {
            throw new Error(`Invalid version number format: Contains non-numeric parts. v1: "${version1}", v2: "${version2}"`);
        }
        // Compare each corresponding part of the version numbers
        const maxLength = Math.max(parts1.length, parts2.length);
        for (let i = 0; i < maxLength; i++) {
            const part1 = parts1[i] ?? 0;
            const part2 = parts2[i] ?? 0;
            if (part1 > part2) {
                return 1;
            }
            if (part1 < part2) {
                return -1;
            }
        }
        return 0;
    };
    const comparison = getComparisonResult(v1, v2);
    switch (operator) {
        case '<':
        case 'lt': return comparison < 0;
        case '<=':
        case 'le': return comparison <= 0;
        case '>':
        case 'gt': return comparison > 0;
        case '>=':
        case 'ge': return comparison >= 0;
        case '=':
        case '==':
        case 'eq': return comparison === 0;
        case '!=':
        case '<>':
        case 'ne': return comparison !== 0;
        default:
            throw new Error(`Invalid comparison operator provided: ${operator}`);
    }
}
;
